import { eventChannel } from 'redux-saga';
import { delay, put, takeLatest, select, take, takeEvery, all, fork, call } from 'redux-saga/effects';

import { fetchExtensions } from 'actions/extensionActions';
import { fetchLanguages } from 'actions/languageActions';
import { fetchFleetbackPack } from 'actions/packActions';

import { restClient } from 'config/store/configureStore';
import * as ActionTypes from 'constants/ActionTypes';
import { DEFAULT_LANGUAGE } from 'constants/LanguageConstants';
import { getLocalStoragePermissionDenied } from 'hooks/useNotificationPermission';
import { loadSession } from 'modules/auth/actions';
import { fetchAllBrands } from 'modules/brands/actions';
import * as BrandTypes from 'modules/brands/actionsType';
import { fetchCountries } from 'modules/countries/actions';

import { FETCH_COUNTRIES_FAILURE, FETCH_COUNTRIES_SUCCESS } from 'modules/countries/actionTypes';
import { fetchAppTranslations, switchLocale } from 'modules/i18n/actions';
import * as i18nTypes from 'modules/i18n/actionTypes';
import { getLocale } from 'modules/i18n/selectors/data';
import {
  subscribe,
  openPermissionModal,
  notificationsBlocked,
  fetchNotificationsDropdown,
  fetchUnreadNotificationsCount,
} from 'modules/notifications/actions';
import { haveNotificationAccess } from 'modules/notifications/selectors/ui/configuration';
import { LOCATION_CHANGE } from 'modules/router/actionTypes';
import { connectSocket } from 'modules/sockets/actions';
import { createNotification } from 'modules/toast-notifications/actions';
import { CURRENT_USER_DATA_SUCCESS } from 'modules/users/actionTypes';
import { isImpersonatingUser } from 'modules/users/selectors/currentUser';

import errorSaga from './error';
import utilsSaga from './utils';
import versionSaga from './version';
import firebase from '../../../firebase';

import {
  getFuelTypes,
  connectionLost,
  fetchAPPThemes,
  applicationLoaded,
  forcePageReloadSuccess,
  connectionEstablished,
  setBrowserIsCompatible,
  applicationLoadedFailure,
  fetchAppConfigurationSuccess,
  fetchAppConfigurationFailure,
  fetchAppConfiguration as fetchAppConfigurationAction,
  fetchHealthCheck as fetchHealthCheckAction,
  fetchSelfServiceWorkflow,
} from '../actions';
import {
  FETCH_APP_CONFIGURATION,
  FETCH_APP_CONFIGURATION_FAILURE,
  FETCH_APP_CONFIGURATION_SUCCESS,
  FORCE_PAGE_RELOAD,
} from '../actionTypes';
import { FAILURE_REQUEST, SUCCESS_REQUEST } from '../constants';
import { applicationIsReady } from '../selectors';
import { getBaseActionType, isActionRequest, simpleRequest } from '../utils';

function* reloadApp() {
  yield delay(5);
  yield put(forcePageReloadSuccess());
}

function* afterChangePage({ payload }) {
  const isStarted = yield select(applicationIsReady);

  if (!isStarted) {
    // Get the languages supported by the app
    yield put(fetchLanguages());
    const { languages = [] } = yield take([ActionTypes.FETCH_LANGUAGES_SUCCESS, ActionTypes.FETCH_LANGUAGES_FAILURE]);

    // Check if the users locale exists in the supported languages
    const locale = yield select(getLocale);
    if (!languages.includes(locale)) {
      // If language not supported, we switch to english if available, otherwise we take the first returned language
      // English is still used as last resort, if nothing else exists or in case of failure
      // NOTE: `switchLocale` will trigger `fetchAppTranslations` automatically
      yield put(switchLocale(languages.find(lang => lang === DEFAULT_LANGUAGE) ?? languages[0] ?? DEFAULT_LANGUAGE));
    } else {
      // Load the translations for the app in the chosen language
      yield put(fetchAppTranslations());
    }

    yield take([i18nTypes.FETCH_TRANSLATIONS_SUCCESS, i18nTypes.FETCH_TRANSLATIONS_FAILURE]);

    // We ask for the user session once at startup
    yield put(loadSession());
  }

  if (payload?.action && payload.action !== 'REPLACE') {
    yield put(fetchHealthCheckAction());
  }
}

function* afterLoadCurrentUser({ payload: userData, meta }) {
  if (!meta?.source) {
    const { id: userId, language: userLanguage } = userData;
    const selectedLanguage = yield select(getLocale);
    const waitOthers = [];

    if (userLanguage && userLanguage !== selectedLanguage) {
      yield put(switchLocale(userLanguage));
      waitOthers.push(take([i18nTypes.FETCH_TRANSLATIONS_SUCCESS, i18nTypes.FETCH_TRANSLATIONS_FAILURE]));
    }

    yield all([
      put(getFuelTypes()),
      put(fetchAPPThemes()),
      put(fetchAllBrands()),
      put(fetchCountries()),
      put(fetchExtensions()),
      put(fetchFleetbackPack()),
      put(fetchSelfServiceWorkflow()),
      put(fetchAppConfigurationAction()),
      put(fetchNotificationsDropdown(userId)),
      put(fetchUnreadNotificationsCount(userId)),
    ]);

    /** Wait and checks all success calls */
    const results = yield all([
      take([FETCH_COUNTRIES_SUCCESS, FETCH_COUNTRIES_FAILURE]),
      take([BrandTypes.FETCH_ALL_BRANDS_SUCCESS, BrandTypes.FETCH_ALL_BRANDS_FAILURE]),
      take([ActionTypes.FETCH_EXTENSIONS_SUCCESS, ActionTypes.FETCH_EXTENSIONS_FAILURE]),
      take([ActionTypes.FETCH_FLEETBACK_PACK_SUCCESS, ActionTypes.FETCH_FLEETBACK_PACK_FAILURE]),
      take([FETCH_APP_CONFIGURATION_SUCCESS, FETCH_APP_CONFIGURATION_FAILURE]),
      ...waitOthers,
    ]);

    const actionError = results.find(({ error }) => error);
    if (actionError) {
      yield put(applicationLoadedFailure(actionError.payload));
    } else {
      const isImpersonating = yield select(isImpersonatingUser);
      if (!isImpersonating) {
        yield firebase.firebaseInitPromise;
        yield put(setBrowserIsCompatible(firebase.isBrowserCompatible));
      }
      yield put(applicationLoaded());
      yield put(connectSocket());

      if (!isImpersonating && firebase.isBrowserCompatible) {
        const isPermissionDenied = yield call(getLocalStoragePermissionDenied, userId);
        const canSeeNotifications = yield select(haveNotificationAccess);
        const notificationsDenied = Notification?.permission === 'denied';
        const mustAskPermission = canSeeNotifications && Notification?.permission === 'default' && !isPermissionDenied;
        const hasNotificationsEnabled = canSeeNotifications && Notification?.permission === 'granted';

        if (mustAskPermission) {
          yield put(openPermissionModal(userId));
        } else if (hasNotificationsEnabled) {
          yield put(subscribe(userId));
        } else if (notificationsDenied) {
          yield put(notificationsBlocked(userId));
        }
      }
    }
  }
}

function* connectionWatcher() {
  const channel = eventChannel(emitter => {
    window.addEventListener('online', emitter);
    window.addEventListener('offline', emitter);
    return () => {
      window.removeEventListener('online', emitter);
      window.removeEventListener('offline', emitter);
    };
  });

  while (true) {
    const { type } = yield take(channel);
    if (type === 'online') {
      yield put(connectionEstablished());
      yield put(
        createNotification({
          type: 'success',
          message: {
            id: 'networks.established',
            defaultMessage: 'Internet connection recovered',
          },
        })
      );
    } else {
      yield put(connectionLost());
      yield put(
        createNotification({
          type: 'error',
          message: {
            id: 'networks.lost',
            defaultMessage: 'Connection internet lost',
          },
        })
      );
    }
  }
}

function* fetchAppConfiguration({ payload, meta }) {
  const { request } = payload;

  try {
    const { data: configuration } = yield call(restClient.request, request);
    yield put(fetchAppConfigurationSuccess(configuration));
  } catch (errorMessage) {
    yield put(fetchAppConfigurationFailure(errorMessage, meta));
  }
}

/** @param {import('modules/global/generic.types').Action<any>} action */
function* asyncActionWatcher({ type, meta: metaRequest }) {
  const requestId = metaRequest['@async-id'];
  const typeBase = type.substring(0, type.length - 7);

  const takeFn = ({ type: responseType, meta = {} }) =>
    (responseType === `${typeBase}SUCCESS` || responseType === `${typeBase}FAILURE`) &&
    meta['@async-id'] === requestId &&
    meta['@async-root'] === type;

  const { type: responseType, payload } = yield take(takeFn);
  const callback = responseType === `${typeBase}SUCCESS` ? metaRequest.resolve : metaRequest.reject;
  yield call(callback, payload);
}

const isAsyncFn = ({ type, meta }) => type.endsWith('REQUEST') && meta?.['@async'];

const isAutoRequest = ({ type, meta }) => type.endsWith('REQUEST') && meta?.['@app-request'];

/**
 * Redux-Saga helper that dispatches a REQUEST action and waits for its SUCCESS or FAILURE response.
 *
 * This is a convenience function that combines `put()` and `take()` effects to simplify
 * the common pattern of dispatching an action and immediately waiting for its result.
 *
 * @generator
 * @function putAndWait
 * @template [R=unknown] The payload the SUCCESS action carries — supplied by the caller
 * @template {{ type: string, payload?: { request?: unknown } }} [A={ type: string, payload?: { request?: unknown } }]
 * @param {A} action - The REQUEST action to dispatch
 * @returns {Generator<*, import('modules/global/generic.types').PutAndWaitResult<R>, *>}
 *          The SUCCESS or FAILURE action that was dispatched in response. Delegate with
 *          `yield* putAndWait<TheSuccessPayload>(action)` and narrow it with `isApiFailure`
 *          (`modules/global/generic.guards`).
 * @throws {Error} If the action is not a valid REQUEST action (must end with 'REQUEST' and have a request payload)
 *
 * @description
 * **How it works:**
 * 1. Validates that the action is a REQUEST action (type ends with 'REQUEST' and has request payload)
 * 2. Extracts the base action type (e.g., 'FETCH_USER' from 'FETCH_USER_REQUEST')
 * 3. Dispatches the action using `put()`
 * 4. Waits for either the SUCCESS or FAILURE response using `take()`
 * 5. Returns the response action
 *
 * **Action Naming Convention:**
 * For this helper to work, actions must follow this naming pattern:
 * - `{BASE}_REQUEST` - The initial request action
 * - `{BASE}_SUCCESS` - The success response action
 * - `{BASE}_FAILURE` - The failure response action
 *
 * **Response Structure:**
 * The returned action will have this structure:
 * - Success: `{ type: '...SUCCESS', payload: <data>, meta: {...} }`
 * - Failure: `{ type: '...FAILURE', payload: <error>, error: true, meta: {...} }`
 *
 * @example
 * // Basic usage - dispatch and wait for response
 * const response = yield putAndWait(fetchUser(userId));
 *
 * @example
 * // Destructure to check success/failure
 * const { payload, error } = yield putAndWait(fetchUser(userId));
 * if (error) {
 *   console.error('Failed to fetch user:', payload);
 * } else {
 *   console.log('User loaded:', payload);
 * }
 *
 * @example
 * // Chain multiple requests sequentially
 * const { payload: user, error: userError } = yield putAndWait(fetchUser(userId));
 * if (!userError) {
 *   const { payload: groups } = yield putAndWait(fetchUserGroups(user.groupIds));
 *   // Use groups...
 * }
 *
 * @example
 * // Use in conditional logic
 * const { error } = yield putAndWait(createCampaign(campaignData));
 * if (!error) {
 *   yield call(navigateToDashboard);
 * }
 *
 * @example
 * // Required action structure
 * const fetchUser = (userId) => ({
 *   type: 'USER/FETCH_REQUEST',  // Must end with 'REQUEST'
 *   payload: {
 *     request: {                  // Must have request object
 *       method: 'GET',            // with method
 *       url: `/api/users/${userId}`, // and url
 *     },
 *   },
 *   meta: {
 *     '@app-request': true,       // Enables automatic request handling
 *   },
 * });
 *
 * @see {@link isActionRequest} - Validates the action format
 * @see {@link getBaseActionType} - Extracts the base action type
 * @see {@link simpleRequest} - The saga that processes REQUEST actions
 */
export function* putAndWait(action) {
  if (isActionRequest(action)) {
    const baseAction = getBaseActionType(action.type);
    yield put(action);
    return yield take([`${baseAction}${SUCCESS_REQUEST}`, `${baseAction}${FAILURE_REQUEST}`]);
  }
  throw new Error('Invalid error - The given params is not a request action');
}

export default function* appSagas() {
  yield takeEvery(isAsyncFn, asyncActionWatcher);
  yield takeEvery(isAutoRequest, simpleRequest);

  yield fork(errorSaga);
  yield fork(utilsSaga);
  yield fork(versionSaga);
  yield fork(connectionWatcher);
  yield takeEvery(LOCATION_CHANGE, afterChangePage);
  yield takeLatest(CURRENT_USER_DATA_SUCCESS, afterLoadCurrentUser);
  yield takeLatest(FORCE_PAGE_RELOAD, reloadApp);
  yield takeLatest(FETCH_APP_CONFIGURATION, fetchAppConfiguration);
}
